import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { settingsApi } from '../../../api/settingsApi';
import { Profession } from '../../../models/settings.ts';

interface ProfessionFormModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    profession?: Profession;
    redirectToQuestions?: boolean;
}

export const ProfessionFormModal: React.FC<ProfessionFormModalProps> = ({
    open,
    onClose,
    onSuccess,
    profession,
    redirectToQuestions = false,
}) => {
    const navigate = useNavigate();
    const DEFAULT_COLOR = '#2E7D32';
    const [professionName, setProfessionName] = useState('');
    const [professionKey, setProfessionKey] = useState('');
    const [professionColor, setProfessionColor] = useState<string>(DEFAULT_COLOR);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [shouldAddQuestions, setShouldAddQuestions] = useState(redirectToQuestions);

    const normalizeColorHex = (value?: string | null): string => {
        const fallback = DEFAULT_COLOR;
        if (!value) {
            return fallback;
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return fallback;
        }
        const formatted = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
        return formatted.toUpperCase();
    };

    useEffect(() => {
        if (profession) {
            setProfessionName(profession.professionName);
            setProfessionKey(profession.professionKey);
            setProfessionColor(normalizeColorHex(profession.colorHex));
            setShouldAddQuestions(false); // Don't show checkbox when editing
        } else {
            setProfessionName('');
            setProfessionKey('');
            setProfessionColor(DEFAULT_COLOR);
            setShouldAddQuestions(redirectToQuestions);
        }
        setError('');
    }, [profession, open, redirectToQuestions]);

    // Auto-generate profession key based on name
    useEffect(() => {
        if (!profession && professionName) {
            const generatedKey = professionName
                .toUpperCase()
                .replace(/\s+/g, '_')
                .replace(/[^A-Z0-9_]/g, '');
            setProfessionKey(generatedKey);
        }
    }, [professionName, profession]);

    const handleSubmit = async () => {
        setError('');
        
        if (!professionName.trim()) {
            setError('Profession name is required');
            return;
        }

        if (!professionKey.trim()) {
            setError('Profession key is required');
            return;
        }
        
        // Validate profession key format
        if (!/^[A-Z0-9_]+$/.test(professionKey)) {
            setError('Profession key must contain only uppercase letters, numbers, and underscores');
            return;
        }

        setLoading(true);

        try {
            let createdProfession;
            const normalizedColor = normalizeColorHex(professionColor);
            if (profession) {
                await settingsApi.updateProfession(profession.id, {
                    professionName: professionName.trim(),
                    professionKey: professionKey.trim(),
                    colorHex: normalizedColor,
                });
            } else {
                createdProfession = await settingsApi.createProfession({
                    professionName: professionName.trim(),
                    professionKey: professionKey.trim(),
                    colorHex: normalizedColor,
                });
            }
            onSuccess();
            handleClose();
            
            // Navigate to question editor if requested
            if (!profession && shouldAddQuestions && createdProfession) {
                navigate(`/questions/profession/${createdProfession.professionKey}?language=EN`);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save profession';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setProfessionName('');
        setProfessionKey('');
        setProfessionColor(DEFAULT_COLOR);
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {profession ? 'Edit Profession' : 'Add New Profession'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <TextField
                        label="Profession Name"
                        fullWidth
                        value={professionName}
                        onChange={(e) => setProfessionName(e.target.value)}
                        sx={{ mb: 2 }}
                        required
                    />
                    <TextField
                        label="Profession Key"
                        fullWidth
                        value={professionKey}
                        onChange={(e) => setProfessionKey(e.target.value.toUpperCase())}
                        helperText="Automatically generated from name. Must contain only uppercase letters, numbers, and underscores."
                        required
                        error={!!(professionKey && !/^[A-Z0-9_]+$/.test(professionKey))}
                    />
                    <TextField
                        label="Accent Color"
                        type="color"
                        fullWidth
                        value={professionColor}
                        onChange={(e) => setProfessionColor(e.target.value)}
                        sx={{ mt: 2 }}
                        InputLabelProps={{ shrink: true }}
                        helperText="Used for worker headers and question pages."
                    />
                    {!profession && redirectToQuestions && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={shouldAddQuestions}
                                    onChange={(e) => setShouldAddQuestions(e.target.checked)}
                                />
                            }
                            label="Add questions immediately after creating"
                            sx={{ mt: 2 }}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : profession ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
